import { Box, FormikSelectInputNative } from '@palmetto/palmetto-components';
import { Field, useFormikContext } from 'formik';
import { useMemo } from 'react';

import { InstallDocumentTypes, InstallPackageFileCategory } from 'types';

import { useGetDesignToolsQuery } from '../../services/designTools';
import FormSection from '../Forms/FormSection';
import {
  DesignDocsDescription,
  NewCommunityDesignDetailsDescription,
} from '../InstallMilestonePackage/InstallationFormSectionDescriptions';
import { DocumentList } from '../MilestonePackagePartials/Documents/DocumentList';

export const Design = ({
  allowArchive,
  showCheckboxes,
  checkboxCategories,
  isNewCommunity = false,
  isPackageEditable = true,
}: {
  allowArchive?: boolean;
  showCheckboxes: boolean;
  checkboxCategories: InstallPackageFileCategory[];
  isNewCommunity?: boolean;
  isPackageEditable?: boolean;
}) => {
  const { data: designTools, isLoading: isAppSettingsLoading } = useGetDesignToolsQuery();
  const { isSubmitting, values } = useFormikContext<{ designTool?: string }>();

  const designToolsOptions = useMemo(() => {
    if (designTools === undefined) return [];

    const mappedDesignTools = Object.keys(designTools).map((key) => {
      return { value: key, label: designTools[key] };
    });

    // A native <select> with no matching <option> auto-selects the first non-disabled
    // option (HTML default-selectedness), silently showing the wrong tool for accounts
    // whose persisted designTool isn't a canonical key (legacy data, casing, etc.). Keep
    // the real value selectable so the field never displays something other than the truth.
    const currentValue = values.designTool;
    const hasCanonicalMatch =
      currentValue !== undefined && Object.prototype.hasOwnProperty.call(designTools, currentValue);
    const optionsWithCurrentValue =
      currentValue && !hasCanonicalMatch
        ? [...mappedDesignTools, { value: currentValue, label: currentValue }]
        : mappedDesignTools;

    return optionsWithCurrentValue.toSorted((a, b) => a.label.localeCompare(b.label));
  }, [designTools, values.designTool]);

  return (
    <FormSection
      description={isNewCommunity ? NewCommunityDesignDetailsDescription : DesignDocsDescription}
      title={isNewCommunity ? 'Design Package Details' : 'Design Package Documents'}
    >
      <DocumentList
        documentTypeFilter={[
          InstallDocumentTypes.planSet,
          InstallDocumentTypes.productionModel,
          InstallDocumentTypes.shadeReport,
        ]}
        title="Design Package"
        baseCategory={'Design Package Documents'}
        allowArchive={allowArchive}
        showCheckboxes={showCheckboxes}
        checkboxCategories={checkboxCategories}
        isPackageEditable={isPackageEditable}
      />
      <Box width="50">
        <Field
          type="text"
          id="designTool"
          name="designTool"
          label="Design Tool"
          options={designToolsOptions}
          component={FormikSelectInputNative}
          isDisabled={isAppSettingsLoading || isSubmitting || !isPackageEditable}
        />
      </Box>
    </FormSection>
  );
};
